// Deterministic repair estimate configuration.
// All ranges are preliminary CAD planning ranges — final pricing is confirmed
// in store after inspection, exact model identification and part availability.

export const DEVICE_CATEGORIES = [
  {
    id: "iphone",
    label: "iPhone",
    blurb: "Every generation, from iPhone 8 to the latest Pro models.",
    icon: "smartphone",
    priceFactor: 1,
    tiers: [
      { id: "older", label: "Older iPhone", hint: "iPhone 8, X and SE-era devices", mult: 0.8 },
      { id: "recent", label: "Recent iPhone", hint: "iPhone 11 – 14 era", mult: 1 },
      { id: "newest", label: "Newest / Pro iPhone", hint: "iPhone 15 – 17 and Pro models", mult: 1.35 },
    ],
  },
  {
    id: "android",
    label: "Android phone",
    blurb: "Samsung, Google Pixel and other Android devices.",
    icon: "smartphone",
    priceFactor: 1,
    tiers: [
      { id: "standard", label: "Standard Android", hint: "Entry and mid-range phones", mult: 0.85 },
      { id: "recent", label: "Recent Android", hint: "Recent mainstream models", mult: 1 },
      { id: "flagship", label: "Flagship Android", hint: "Galaxy S / Ultra, Pixel Pro and similar", mult: 1.3 },
    ],
  },
  {
    id: "tablet",
    label: "Tablet",
    blurb: "iPad and Android tablets of all sizes.",
    icon: "tablet",
    priceFactor: 1.1,
    tiers: [
      { id: "standard", label: "Standard tablet", hint: "Everyday and older tablets", mult: 0.9 },
      { id: "recent", label: "Recent tablet", hint: "Recent iPad and Android models", mult: 1.05 },
      { id: "pro", label: "Pro tablet", hint: "iPad Pro and large premium tablets", mult: 1.35 },
    ],
  },
  {
    id: "laptop",
    label: "Laptop",
    blurb: "MacBook and Windows laptops, hardware and software.",
    icon: "laptop",
    priceFactor: 1.3,
    tiers: [
      { id: "standard", label: "Standard laptop", hint: "Everyday and older laptops", mult: 0.9 },
      { id: "recent", label: "Recent laptop", hint: "Recent mainstream and ultrabook models", mult: 1.1 },
      { id: "pro", label: "Gaming / Pro laptop", hint: "Gaming rigs, MacBook Pro and workstations", mult: 1.4 },
    ],
  },
];

// Owner-set entry pricing: EVERY repair starts at $79 — batteries, screens,
// ports, cameras, everything — scaling up for premium devices and parts.
export const MIN_QUOTE = 79;

export const ISSUES = [
  {
    id: "screen",
    label: "Broken screen",
    hint: "Cracks, dead pixels or touch problems",
    icon: "crack",
    base: [79, 380],
  },
  {
    id: "battery",
    label: "Battery replacement",
    hint: "Fast drain, shutdowns, swelling or poor health",
    icon: "battery",
    base: [79, 150],
  },
  {
    id: "charging",
    label: "Charging problem",
    hint: "Loose port, no charge or intermittent power",
    icon: "plug",
    base: [79, 160],
  },
  {
    id: "backglass",
    label: "Broken back glass",
    hint: "Cracked rear glass or housing damage",
    icon: "layers",
    base: [79, 260],
  },
  {
    id: "camera",
    label: "Camera issue",
    hint: "Lens damage, focus problems or a failed module",
    icon: "camera",
    base: [79, 290],
  },
  {
    id: "water",
    label: "Water damage",
    hint: "Moisture exposure, corrosion or no power",
    icon: "droplets",
    base: [79, 320],
  },
  {
    id: "software",
    label: "Software issue",
    hint: "Crashes, updates, boot or account problems",
    icon: "refresh",
    base: [79, 140],
  },
  {
    id: "data",
    label: "Data recovery",
    hint: "Photos, files or device-access problems",
    icon: "harddrive",
    base: [79, 450],
  },
];

export const PRICE_FACTORS = [
  "Exact model and part quality available for it",
  "How far the damage has spread inside the device",
  "Part availability and supplier pricing that week",
  "Any extra issues found during the free inspection",
];

const round10 = (n) => Math.round(n / 10) * 10;

export function getCategory(id) {
  return DEVICE_CATEGORIES.find((c) => c.id === id) || null;
}

export function getTier(categoryId, tierId) {
  const cat = getCategory(categoryId);
  return cat ? cat.tiers.find((t) => t.id === tierId) || null : null;
}

export function getIssue(id) {
  return ISSUES.find((i) => i.id === id) || null;
}

export function computeEstimate(categoryId, tierId, issueId) {
  const cat = getCategory(categoryId);
  const tier = getTier(categoryId, tierId);
  const issue = getIssue(issueId);
  if (!cat || !tier || !issue) return null;
  const f = cat.priceFactor * tier.mult;
  return {
    low: Math.max(MIN_QUOTE, round10(issue.base[0] * f)),
    high: round10(issue.base[1] * f),
  };
}
